// FacilitatorLive.jsx
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { authedFetch } from '../utils/authedFetch';
import ImagingLibraryBrowser from './ImageLibraryPicker';
import FeedbackButton from '../utils/feedbackButton';
import io from 'socket.io-client';
import {
  Box, Paper, Typography, Button, TextField, Chip,
  Modal, Tooltip, Stack, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Checkbox,
  Select, MenuItem, Alert, 
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import BloodSetEditor from './scenariobuilder/BloodsGenerator';
import SendIcon from '@mui/icons-material/Send';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { supabase } from '../supabaseClient';
import dayjs from 'dayjs';

//const SOCKET_URL = 'http://localhost:4000';
const SOCKET_URL = process.env.REACT_APP_API_URL;
const ACCENT = 'rgb(219, 234, 254)';   // #DBEAFE
const ACCENT_BORDER = '#60A5FA';


function formatHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${sec.toString().padStart(2,'0')}`;
}

export default function FacilitatorLive() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, session, authLoading } = useAuth();
  const scenarioId = location?.state?.scenarioId;
  const API_BASE = process.env.REACT_APP_API_URL;
  // --- sound refs/state ---
    const audioRef = useRef(null);
    const soundReadyRef = useRef(false);
  
  const socketRef = useRef(null);
  const [pin, setPin] = useState(null);
  const [room, setRoom] = useState(null);
  const [participantConnected, setParticipantConnected] = useState(false);
  const [showPinModal, setShowPinModal] = useState(true);
  const [durationSec, setDurationSec] = useState(600);
  const [startAt, setStartAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [islive, setIsLive] = useState(false);
  const [liveScenarioId, setLiveScenarioId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [bloodSets, setBloodSets] = useState([]);
  const [obsSets, setObsSets] = useState([]);
  const [activeBloodSet, setActiveBloodSet] = useState(null);
  const [activeObsSet, setActiveObsSet] = useState(null);
  const [sendingBloods, setSendingBloods] = useState(false);
  const [sendingObs, setSendingObs] = useState(false);
  const [addBloodsOpen, setAddBloodsOpen] = useState(false);
  const [addObsOpen, setAddObsOpen] = useState(false);
  const [editorData, setEditorData] = useState([]);   // BloodSetEditor returns an array via onChange
  const [obseditorData, setObsEditorData] = useState([]);
  const [savingNewBloods, setSavingNewBloods] = useState(false);
  const [savingNewObs, setSavingNewObs] = useState(false);   
  const [requests, setRequests] = useState([]);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveReq, setResolveReq] = useState(null); // { id, type, ... }
  const [resolveSet, setResolveSet] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [actions, setActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [newActionType, setNewActionType] = useState('request');
  const [newActionDescription, setNewActionDescription] = useState('');
  const [newActionComment, setNewActionComment] = useState('');
  const [savingAction, setSavingAction] = useState(false);
  const [endSessionBtn, setEndSessionBtn] = useState(false);
  const [endingScenario, setEndingScenario] = useState(false);
  const [participantName, setParticipantName] = useState('');

  // **======== Image selector ========**
  const [imagingOpen, setImagingOpen] = useState(false);
  const [selectedImagingItem, setSelectedImagingItem] = useState(null);
  const [sendingImaging, setSendingImaging] = useState(false);
  const [imagingResolveReq, setImagingResolveReq] = useState(null);

  const openImagingModal = () => {
    setSelectedImagingItem(null);
    setImagingOpen(true);
  };

  const closeImagingModal = () => {
    if (sendingImaging) return;

    setImagingOpen(false);
    setSelectedImagingItem(null);
    setImagingResolveReq(null);
  };

  // Send imaging to participant
  const handleSendImaging = async () => {
    if (!selectedImagingItem || !liveScenarioId) return;

    setSendingImaging(true);

    try {
      const res = await authedFetch({
        accessToken: session?.access_token,
        apiBase: API_BASE,
        path: '/api/sendimaging',
        navigate,
        options: {
          method: 'POST',
          body: JSON.stringify({
            livescenarioid: liveScenarioId,
            scenarioid: scenarioId,
            room,

            image_id: selectedImagingItem.id,
            image_source: selectedImagingItem.source || 'public',
            library_key:
              selectedImagingItem.library_key ||
              `${selectedImagingItem.source || 'public'}:${selectedImagingItem.id}`,

            bucket: selectedImagingItem.bucket,
            file_path: selectedImagingItem.file_path,
            public_url: selectedImagingItem.public_url || null,

            title: selectedImagingItem.title,
            modality: selectedImagingItem.modality,
            study_type:
              selectedImagingItem.study_type || selectedImagingItem.image_type || null,
            body_part: selectedImagingItem.body_part || null,
            preview_label: selectedImagingItem.preview_label || null,
            description: selectedImagingItem.description || null,
            report: selectedImagingItem.report || null,
          }),
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      if (imagingResolveReq?.id) {
        const fulfillRes = await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/requests/${imagingResolveReq.id}/fulfill`,
          navigate,
          options: {
            method: 'POST',
            body: JSON.stringify({ fulfilled: true, room }),
          },
        });

        if (!fulfillRes.ok) {
          throw new Error(`Fulfil failed: HTTP ${fulfillRes.status}`);
        }

        setRequests((prev) =>
          prev.map((r) =>
            r.id === imagingResolveReq.id
              ? { ...r, fulfilled: true, locked: true }
              : r
          )
        );
      }

      closeImagingModal();
    } catch (err) {
      console.error('Error sending imaging:', err);
      alert('Failed to send imaging.');
    } finally {
      setSendingImaging(false);
    }
  };

  // **======== Obs editor ========**
  const [obs, setObs] = useState({});
  const handleChange = (field, value) => setObs({ ...obs, [field]: value });
  
  const handleSave = () => {
    const newEntry = {
      ...obs
    };
    handleConfirmAddObs()
    // Reset for next entry
    setObs({  });
    
  };
  
  const userId = 1;  
  const liveIdRef = useRef(null);
    
    useEffect(() => {
    }, [editorData]);

    // Prepare the ding audio and unlock after first user gesture
    useEffect(() => {
    const a = new Audio('/sounds/request.mp3');
    a.preload = 'auto';
    a.volume = 0.4;
    audioRef.current = a;

    const unlock = () => {
        a.play()
        .then(() => {
            a.pause();
            a.currentTime = 0;
            soundReadyRef.current = true;
        })
        .catch(() => {});
    };

    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
    }, []);

    const playDing = useCallback(() => {
    if (!soundReadyRef.current || !audioRef.current) return;

    const a = new Audio(audioRef.current.src);
    a.volume = audioRef.current.volume;
    a.play().catch((err) => console.error('ding failed', err));
    }, []);

    useEffect(() => {
        liveIdRef.current = liveScenarioId;
    }, [liveScenarioId]);
    
    //Socket routes useEffect
    useEffect(() => {
        const socket = io(SOCKET_URL, { transports: ['websocket'] });
        socketRef.current = socket;

        socket.on('connect', () => {
        if (scenarioId) socket.emit('facilitator:init', { scenarioId });
        });

        socket.on('facilitator:pin', ({ pin }) => setPin(pin));

        socket.on('pair:ready', ({ room, participantName }) => {
        setRoom(room);
        setParticipantName(participantName || '');
        setParticipantConnected(true);
        setShowPinModal(false);
        });

        socket.on('scenario:started', ({ startAt, durationSec }) => {
        setStartAt(startAt);
        setDurationSec(durationSec);
        });

        // New request created
        const onCreated = (p) => {
            playDing()
            // discard if for different live scenario
            if (p?.livescenarioid !== liveIdRef.current) return;
            // prepend new row (newest first)
            setRequests(prev => [
            { id: p.id, type: p.type, createdAt: p.createdAt, message:p.message, fulfilled: !!p.fulfilled },
            ...prev
            ]);
        };

        const onLegacy = (p) => {
            // legacy event had only livescenarioid; just refresh list
            if (p?.livescenarioid !== liveIdRef.current) return;
        };

        // Fulfill request
        const onFulfilled = ({ id, fulfilled }) => {
            setRequests(prev => prev.map(r => r.id === id ? { ...r, fulfilled: !!fulfilled } : r));
        };

        // New action created
        const onActionCreated = (a) => {
          if (a?.livescenarioid !== liveIdRef.current) return;
          setActions(prev => [
            {
              id: a.id,
              createdAt: a.createdAt,
              actionTime: a.actionTime,
              type: a.type,
              description: a.description,
              comment: a.comment,
            },
            ...prev
          ]);
        };

        socket.on('request:created', onCreated);
        socket.on('participant:request_obs', onLegacy);
        socket.on('request:fulfilled', onFulfilled);
        socket.on('action:created', onActionCreated);

        socket.on('peer:disconnected', ({ role, reason }) => {
          if (role !== 'participant') return;

          setParticipantConnected(false);
          setParticipantName('');
          
          // Only reopen the PIN modal if the scenario has not started yet
          if (!running) {
            setShowPinModal(true);
          }

          console.log('Participant disconnected:', reason);
        });

        socket.on('error:event', (payload) => console.error('Server error:', payload));

        return () => socket.disconnect();
    }, [scenarioId]);

    useEffect(() => {
        if (!startAt) return;
        const tick = () => setElapsed((Date.now() - startAt) / 1000);
        tick();
        const id = setInterval(tick, 250);
        return () => clearInterval(id);
    }, [startAt]);

    //Fulfill request
    const toggleFulfilled = async (row) => {
        if (row.locked) return; // auto-fulfilled; do nothing

        const next = !row.fulfilled;
        setRequests(prev => prev.map(r => r.id === row.id ? { ...r, fulfilled: next } : r));

        try {
            await fetch(`${API_BASE}/api/requests/${row.id}/fulfill`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fulfilled: next, method: 'manual', room }),
            });

        } catch (e) {
            // revert on error
            setRequests(prev => prev.map(r => r.id === row.id ? { ...r, fulfilled: !next } : r));
            console.error('toggle fulfilled failed', e);
        }
    };

    //Send bloods to participant
    const handleSendBloods = async () => {
        if (!activeBloodSet || !liveScenarioId) return;
        setSendingBloods(true);
        try {
        await fetch(`${API_BASE}/api/sendbloods`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
            livescenarioid: liveScenarioId,
            bloodsid: activeBloodSet.id,
            room,
            }),
        });

        setBloodSets(prev =>
            prev.map(s => (s.id === activeBloodSet.id ? { ...s, visible: true } : s))
        );
        setActiveBloodSet(prev => (prev ? { ...prev, visible: true } : prev));
        } catch (err) {
        console.error('Error sending bloods:', err);
        alert('Failed to send bloods. Please try again.');
        } finally {
        setSendingBloods(false);
        setActiveBloodSet(null)
        }
    };

    //Send obs to participant
    const handleSendObs = async () => {
        if (!activeObsSet || !liveScenarioId) return;
        setSendingObs(true);
        try {
        await fetch(`${API_BASE}/api/sendobs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
            livescenarioid: liveScenarioId,
            obssid: activeObsSet.id,
            room,
            }),
        });

        setObsSets(prev =>
            prev.map(s => (s.id === activeObsSet.id ? { ...s, visible: true } : s))
        );
        setActiveObsSet(prev => (prev ? { ...prev, visible: true } : prev));
        } catch (err) {
        console.error('Error sending obs:', err);
        alert('Failed to send obs. Please try again.');
        } finally {
        setSendingObs(false);
        setActiveObsSet(null)
        }
    };

    //Manually add participant action
    const handleAddAction = async () => {
      if (!liveScenarioId || !newActionDescription.trim()) return;

      setSavingAction(true);
      try {
        const res = await fetch(`${API_BASE}/api/addaction`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            livescenarioid: liveScenarioId,
            room,
            action_type: newActionType,
            action_description: newActionDescription.trim(),
            action_comment: newActionComment.trim() || '-',
          }),
        });
      } catch (err) {
        console.error('Error adding action:', err);
        alert('Failed to add action.');
      } finally {
        setNewActionType('request');
        setNewActionDescription('');
        setNewActionComment('');
        setSavingAction(false);
      }
    };

    //Start the scenario
    const handleStart = async () => {
        if (!room) return;
        setStarting(true);
        try {
          const res = await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: '/api/createlive',
            navigate,
            options: {
              method: 'POST',
              body: JSON.stringify({
                scenarioid: scenarioId,
                userid: userId,
                participant: participantName
                }),
            },
          });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const liveId = data.livescenarioid ?? data.id;
        setLiveScenarioId(liveId);

        const sets = Array.isArray(data?.imported?.bloodSets) ? data.imported.bloodSets : [];
        setBloodSets(sets);

        const obs = Array.isArray(data?.imported?.obsSets) ? data.imported.obsSets : [];
        setObsSets(obs);

        socketRef.current?.emit('scenario:start', { room, livescenarioid: liveId });
        } catch (err) {
        console.error('Error starting new scenario:', err);
        alert('Failed to start scenario. Please try again.');
        } finally {
        setStarting(false);
        setIsLive(true);
        }
    };

    //Click stop button
    const handleStop = () => {
        if (!room) return;
        setEndSessionBtn(true);
    };

    //Stop the scenario
    const handleEndScenario = async () => {
      if (!room || endingScenario) return;

      setEndingScenario(true);

      try {
        socketRef.current?.emit('scenario:stop', { room });

        const res = await fetch(`${API_BASE}/api/endsession`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            livescenarioid: liveScenarioId,
            scenarioId,
          }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        navigate('/app/summary', {
          state: { livescenarioid: liveScenarioId },
        });
      } catch (err) {
        console.error('Error ending scenario:', err);
        alert('Failed to end scenario. Please try again.');
        setEndingScenario(false);
      }
    };

    const parseRequestMsg = (raw) => {
        if (!raw) return null;
        // try JSON first (we send JSON.stringify([...]) from participant for bloods)
        try {
            const val = JSON.parse(raw);
            return Array.isArray(val) ? val : String(raw);
        } catch {
            // fallback: plain text / CSV string
            return String(raw);
        }
    };

    const renderRequestMsg = (msg) => {
        if (!msg) return <Typography variant="body2" color="text.secondary">—</Typography>;
        if (Array.isArray(msg)) {
            return (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {msg.map((label, i) => (
                <Chip key={i} size="small" label={label} sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
            </Stack>
            );
        }
        return <Typography variant="body2">{msg}</Typography>;
    };

    // RESOLVE REQUEST
    const openResolve = (row) => {
      setResolveReq(row);
      setResolveSet(null);
      setResolveOpen(true);
    };
    const closeResolve = () => {
      setResolveOpen(false);
      setResolveReq(null);
      setResolveSet(null);
    };

    const handleResolveSend = async () => {
    if (!resolveReq || !resolveSet || !liveScenarioId) return;
    setResolving(true);
    try {
        if (resolveReq.type === 'obs') {
        // send obs
        await fetch(`${API_BASE}/api/sendobs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
            livescenarioid: liveScenarioId,
            obssid: resolveSet.id,
            room
            }),
        });
        setObsSets(prev => prev.map(s => s.id === resolveSet.id ? ({ ...s, visible: true }) : s));
        } else if (resolveReq.type === 'bloods') {
        // send bloods
        await fetch(`${API_BASE}/api/sendbloods`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
            livescenarioid: liveScenarioId,
            bloodsid: resolveSet.id,
            room
            }),
        });
        setBloodSets(prev => prev.map(s => s.id === resolveSet.id ? ({ ...s, visible: true }) : s));
        }

        // mark request fulfilled
        await fetch(`${API_BASE}/api/requests/${resolveReq.id}/fulfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fulfilled: true, room }),
        });
        setRequests(prev =>
            prev.map(r => r.id === resolveReq.id ? { ...r, fulfilled: true, locked: true } : r)
        );

        closeResolve();
    } catch (e) {
        console.error('resolve send failed', e);
        alert('Failed to send and fulfil. Please try again.');
    } finally {
        setResolving(false);
    }
    };

    // Opens Add Obs modal
    const openAddObs = () => {
    setObsEditorData([]);         // start clean each time
    setAddObsOpen(true);
    };

    // cancel modal
    const closeAddObs = () => {
    setAddObsOpen(false);
    setObsEditorData([]);
    };

    const closeEndSession = () => {
      setEndSessionBtn(false);
    }

    // Opens Add bloods modal
    const openAddBloods = () => {
    setEditorData([]);         // start clean each time
    setAddBloodsOpen(true);
    };

    // cancel modal
    const closeAddBloods = () => {
    setAddBloodsOpen(false);
    setEditorData([]);
    };

    const handleConfirmAddObs = async() => {
    setSavingNewObs(true)

    try {
      const clean = (value) => value === '' ? null : value;

      await fetch(`${API_BASE}/api/save-scenarioobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenarioid: scenarioId,
          name: obs.name,
          tempo: true,
          livescenarioid: liveScenarioId,
          rr: clean(obs.rr),
          spo2: clean(obs.spo2),
          sbp: clean(obs.sbp),
          pulse: clean(obs.pulse),
          temp: clean(obs.temp),
          avpu: obs.avpu || null,
          air: obs.air || null,
          o2: clean(obs.o2),
          device: obs.device || null
        }),
      });
    } catch (err) {
        console.error('Error adding bloods:', err);
        alert('Failed to save add blood results.');
    } finally {
      setSavingNewObs(false);
      await refreshObsSets();
      closeAddObs();
    }
    };

    const refreshObsSets = async () => {
        const res = await fetch(`${API_BASE}/api/scenario/getobs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({livescenarioid: liveScenarioId, scenarioid: scenarioId})
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setObsSets(data.obsSets);
    };

    const handleConfirmAddBloods = async() => {
        setSavingNewBloods(true)
        //Save as temp bloods
        try {
            for (const bloodSet of editorData) {
            await fetch(`${API_BASE}/api/save-scenariobloods`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                scenarioid: scenarioId,
                livescenarioid: liveScenarioId,
                name: bloodSet.name,
                temp: true,
                data: Object.fromEntries(
                    bloodSet.groups.map(group => [
                    group.label,
                    group.values.map(test => ({
                        name: test.name,
                        value: test.value,
                        units: test.units,
                        refLow: test.refLow,
                        refHigh: test.refHigh,
                    }))
                    ])
                )
                }),
            });
            }
      } catch (err) {
        console.error('Error adding bloods:', err);
        alert('Failed to save add blood results.');
      } finally {
        setSavingNewBloods(false);
        closeAddBloods();
        await refreshBloodSets();
      }};

    const refreshBloodSets = async () => {
        const res = await fetch(`${API_BASE}/api/scenario/getbloodsets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({livescenarioid: liveScenarioId, scenarioid: scenarioId})
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setBloodSets(data.bloodSets);
    };

  const running = !!startAt;
  const newestUnfulfilledRequestId = requests.find((r) => !r.fulfilled)?.id;

  return (
  <Box sx={{ height: '100%', width: '100%', bgcolor: '#F8FAFF', p: 2 }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '250px minmax(0, 1fr)' },
        gap: 2,
        marginRight:'30px',
        height: '100%',
        alignItems: 'start',
      }}
    >
      {/* Left sidebar */}
      <Paper
        elevation={0}
        sx={{
          position: { md: 'sticky' },
          top: { md: 2 },
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 3,
          bgcolor: 'white',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 1.25,
            height:'50px',
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display:'flex',
            alignItems:'center',
            justifyContent:'center'
          }}>
                {!running ? (
                  <Button
                    variant="contained"
                    onClick={handleStart}
                    disabled={!participantConnected || running || !room || starting}
                    sx={{ textTransform: 'none', marginLeft:'5px' }}
                  >
                    {starting ? '…' : 'Start Scenario'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleStop}
                    disabled={!running}
                    sx={{ textTransform: 'none', marginLeft:'10px' }}
                  >
                    End scenario
                  </Button>
                )}
        </Box>

        <Stack spacing={1.5} sx={{ p: 1, marginBottom:'5px' }}>
            {/* Send Bloods sidebar */}
            <ActionCard
                title="Send Bloods"
                rightIcon={
                  <Tooltip
                    title="Create blood sets"
                    placement="top"
                  >
                    <IconButton
                        variant="contained"
                        size="small"
                        color='success'
                        sx={{ textTransform: 'none', minWidth:'30px'}}
                        disabled={!islive}
                        onClick={openAddBloods}
                        >
                        <AddBoxOutlinedIcon sx={{fontSize:'36px'}}></AddBoxOutlinedIcon>
                    </IconButton>
                  </Tooltip>       
            }>
                <Stack
                    spacing={1}
                    alignItems="center"
                    sx={{ width: '100%' }}
                >

                <Autocomplete
                    sx={{ flex: 1, width:'100%' }}
                    size="small"
                    options={bloodSets}
                    value={activeBloodSet}
                    disabled={!islive}
                    onChange={(_, v) => setActiveBloodSet(v)}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    getOptionLabel={(o) =>
                    o ? `${o.setname}${o.groupnames_csv ? ` (${o.groupnames_csv})` : ''}` : ''
                    }
                    renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select saved bloods"
                        placeholder="Choose a set…"
                    />
                    )}
                    renderOption={(props, option) => (
                    <li
                        {...props}
                        key={option.id}
                        style={{ opacity: option.visible ? 0.55 : 1 }}
                        aria-disabled={option.visible ? 'true' : 'false'}
                    >
                        <span style={{ flex: 1 }}>
                        {option.setname}
                        {option.groupnames_csv ? ` (${option.groupnames_csv})` : ''}
                        </span>
                        {option.visible ? (
                        <Chip size="small" label="Sent" />
                        ) : (
                        <Chip size="small" label="Hidden" variant="outlined" />
                        )}
                    </li>
                    )}
                />
                <Button
                  disabled={!activeBloodSet || activeBloodSet.visible || sendingBloods}
                  color='success'
                  size="small"
                  sx={{ textTransform: 'none', marginTop: '5px', minWidth: 110 }}
                  onClick={handleSendBloods}
                >
                  {sendingBloods ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )}
                </Button>
            </Stack>
            </ActionCard>

            {/* Send Obs sidebar */}
            <ActionCard
                title="Send Observations"
                rightIcon={
                  <Tooltip
                    title="Create observations"
                    placement="top"
                  >
                    <IconButton
                        variant="contained"
                        disabled={!islive}
                        size="small"
                        color='success'
                        sx={{ textTransform: 'none', minWidth:'30px'}}
                        onClick={openAddObs}
                        >
                        {savingAction ? (
                          <CircularProgress size={20} />
                        ) : (
                        <AddBoxOutlinedIcon sx={{fontSize:'36px'}}></AddBoxOutlinedIcon>
                        )}
                    </IconButton>
                  </Tooltip>       
            }>
                <Stack
                    spacing={1}
                    alignItems="center"
                    sx={{ width: '100%' }}>

                    <Autocomplete
                        sx={{ flex: 1, width:'100%' }}
                        size="small"
                        disabled={!islive}
                        options={obsSets}
                        value={activeObsSet}
                        onChange={(_, v) => setActiveObsSet(v)}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                        getOptionLabel={(o) => (o ? `${o.name}` : '')}
                        renderInput={(params) => (
                        <TextField {...params} label="Select saved obs" placeholder="Choose a set…" />
                        )}
                        renderOption={(props, option) => (
                        <li {...props} 
                            key={option.id}                         
                            style={{ opacity: option.visible ? 0.55 : 1 }}
                            aria-disabled={option.visible ? 'true' : 'false'}
                        >
                            <span style={{ flex: 1 }}>{option.name}</span>
                            {option.visible ? (
                            <Chip size="small" label="Visible" />
                            ) : (
                            <Chip size="small" label="Hidden" variant="outlined" />
                            )}
                        </li>
                        )}/>

                    <Button
                      color="success"
                      onClick={handleSendObs}
                      disabled={!activeObsSet || activeObsSet.visible || sendingObs}
                      sx={{ minWidth: 40, height: 20 }}
                      >
                      {sendingObs ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    </Button>

                </Stack>
                </ActionCard>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ImageOutlinedIcon />}
                  disabled={!islive}
                  onClick={() => {
                    setImagingResolveReq(null);
                    openImagingModal();
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Send imaging
                </Button>
        </Stack>
      </Paper>

      {/* Main content */}
      <Box sx={{ minWidth: 0 }}>
        <Stack spacing={2} sx={{ mx: 'auto' }}>
          {/* Sticky top bar */}
          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              top: 2,
              zIndex: 10,
              border: `1px solid ${ACCENT_BORDER}`,
              borderRadius: 3,
              bgcolor: 'white',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                height:'40px',
                bgcolor: ACCENT,
                borderBottom: `1px solid ${ACCENT_BORDER}`,
                borderTopLeftRadius: '30px',
                borderTopRightRadius: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontWeight: 800 }}>Live Scenario Controls</Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  icon={<PersonIcon />}
                  color={participantConnected ? 'success' : 'default'}
                  label={participantConnected ? `Participant connected - ${participantName}` : 'Waiting…'}
                />
                <Chip
                  size="small"
                  icon={<AccessTimeIcon />}
                  label={formatHMS(elapsed)}
                  sx={{ bgcolor: 'white', border: `1px solid ${ACCENT_BORDER}` }}
                />
              </Stack>
            </Box>

            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Room ${room || '—'}`}
                  sx={{
                    bgcolor: 'white',
                    border: `1px solid ${ACCENT_BORDER}`,
                    fontWeight: 700,
                  }}
                />

                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  
                </Typography>
              </Stack>
              {!participantConnected && (
                <Typography variant="body2" color="text.secondary">
                  PIN will be shown in the modal until a participant connects.
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Middle row: Participant requests | Facilitator actions */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: '0.9fr 1fr',
              },
              gap: 2,
              alignItems: 'start',
            }}
          >
            {/* Participant requests */}
            <SectionCard
              title="Participant requests"
              subtitle="Respond to requests."
            >
              <Box sx={{ p: 2 }}>
                {requests.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No requests yet.
                  </Typography>
                ) : (
                  <Table size="small" sx={{ bgcolor: 'white', borderRadius: 1, overflow: 'hidden' }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F3F4F6' }}>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Request</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}></TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((r) => {
                        const created = r.createdAt ?? r.created_at;
                        const reqType = (r.type ?? r.request_type ?? '').toLowerCase();
                        const rawMsg = r.message ?? r.requestmsg ?? null;
                        const msg = parseRequestMsg(rawMsg);

                        return (
                          <TableRow
                            key={r.id}
                            hover
                            sx={{
                              bgcolor:
                                r.id === newestUnfulfilledRequestId
                                  ? 'rgba(254, 243, 199, 0.9)'
                                  : 'inherit',
                              outline:
                                r.id === newestUnfulfilledRequestId
                                  ? '2px solid #F59E0B'
                                  : 'none',
                              outlineOffset: '-2px',
                              '&:hover': {
                                bgcolor:
                                  r.id === newestUnfulfilledRequestId
                                    ? 'rgba(253, 230, 138, 0.95)'
                                    : undefined,
                              },
                            }}
                          >
                            <TableCell>
                              {created ? dayjs(created).format('HH:mm') : '—'}
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize', textAlign: 'center' }}>
                              <Stack spacing={0.5} alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {reqType || '—'}
                                </Typography>

                                {!r.fulfilled && r.id === newestUnfulfilledRequestId && (
                                  <Chip
                                    size="small"
                                    label="New"
                                    color="warning"
                                    sx={{ fontWeight: 700 }}
                                  />
                                )}

                                {r.fulfilled && (
                                  <Chip size="small" label="Fulfilled" color="success" variant="outlined" />
                                )}
                              </Stack>
                            </TableCell>

                            <TableCell sx={{textAlign:'center', alignItems:'center'}}>{renderRequestMsg(msg)}</TableCell>

                            <TableCell>
                              <Stack direction="row" spacing={1} style={{ justifyContent: 'center' }}>
                                <Tooltip
                                  title={
                                    r.locked
                                      ? 'Auto-fulfilled by sending results'
                                      : 'Toggle fulfilled'
                                  }
                                >
                                  <span>
                                    <Checkbox
                                      checked={!!r.fulfilled}
                                      onChange={() => toggleFulfilled(r)}
                                      disabled={!!r.locked}
                                      inputProps={{ 'aria-label': 'Toggle fulfilled' }}
                                    />
                                  </span>
                                </Tooltip>
                                {reqType === 'obs' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openResolve({ ...r, kind: 'obs' })}
                                    disabled={r.locked}
                                  >
                                    {r.locked ? 'Sent' : 'Send'}
                                  </Button>
                                )}
                                {reqType === 'bloods' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      openResolve({ ...r, kind: 'bloods', groups: msg })
                                    }
                                    disabled={r.locked}
                                  >
                                    {r.locked ? 'Sent' : 'Send'}
                                  </Button>
                                )}
                                {reqType === 'imaging' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      setImagingResolveReq(r);
                                      openImagingModal();
                                    }}
                                    disabled={r.locked}
                                  >
                                    {r.locked ? 'Sent' : 'Send'}
                                  </Button>
                                )}
                                {reqType !== 'obs' && reqType !== 'bloods' && reqType !== 'imaging' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openResolve(r)}
                                  >
                                    Resolve
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </SectionCard>

            {/* Participant actions */}
            <SectionCard
              title="Participant actions"
              subtitle="Track or add actions."
            >
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, maxHeight:'400px' }}>
                {/* Add new action form */}
                <Box
                  sx={{
                    p: 1.5,
                    border: '1px solid #E5E7EB',
                    borderRadius: 2,
                    bgcolor: '#F8FAFF',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '70px minmax(50px, 1.3fr) minmax(50px, 1.3fr) minmax(50px, 1.3fr) minmax(33px,33px)',
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      size="small"
                      disabled={true}
                      value={dayjs().format('HH:mm')}
                    />

                    <Select
                      size="small"
                      value={newActionType}
                      onChange={(e) => setNewActionType(e.target.value)}
                      sx={{ bgcolor: 'white' }}
                    >
                      <MenuItem value="intervention">Intervention</MenuItem>
                      <MenuItem value="request">Request</MenuItem>
                      <MenuItem value="result">Result</MenuItem>
                      <MenuItem value="message">Message</MenuItem>
                      <MenuItem value="medication">Medication</MenuItem>
                      <MenuItem value="note">Note</MenuItem>
                    </Select>

                    <TextField
                      size="small"
                      fullWidth
                      value={newActionDescription}
                      onChange={(e) => setNewActionDescription(e.target.value)}
                      placeholder="Enter action description"
                      label="Description"
                    />

                    <TextField
                      size="small"
                      fullWidth
                      value={newActionComment}
                      onChange={(e) => setNewActionComment(e.target.value)}
                      placeholder="Optional comment"
                      label="Comment"
                    />

                    <IconButton
                      color="success"
                      onClick={handleAddAction}
                      disabled={savingAction || !newActionDescription.trim()}
                      sx={{
                        border: '1px solid #D1D5DB',
                        borderRadius: 2,
                        bgcolor: 'white',
                        height: 40,
                        width: 40,
                      }}
                    >
                      {savingAction ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AddBoxOutlinedIcon />
                      )}
                    </IconButton>
                  </Box>
                </Box>

                {/* Scrollable actions table */}
                {loadingActions ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading actions…
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ bgcolor: 'white', borderRadius: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 70 }}>
                            Time
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 120 }}>
                            Type
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>
                            Description
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>
                            Comment
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {actions.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}
                            >
                              No actions yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          actions.map((a) => (
                            <TableRow key={a.id} hover>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {a.actionTime ? dayjs(a.actionTime).format('HH:mm') : '—'}
                              </TableCell>

                              <TableCell sx={{ textAlign: 'center', textTransform: 'capitalize' }}>
                                {a.type || '—'}
                              </TableCell>

                              <TableCell sx={{ textAlign: 'center' }}>
                                {a.description || '—'}
                              </TableCell>

                              <TableCell sx={{ textAlign: 'center', fontSize: '12px' }}>
                                {a.comment || '—'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </SectionCard>
          </Box>

          {/* PIN modal */}
          <Modal open={showPinModal} onClose={() => {}}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'white',
                borderRadius: 3,
                border: `1px solid ${ACCENT_BORDER}`,
                boxShadow: 24,
                overflow: 'hidden',
                minWidth: 380,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  bgcolor: ACCENT,
                  borderBottom: `1px solid ${ACCENT_BORDER}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>Connect participant</Typography>
                <Chip size="small" label={scenarioId ? `Scenario #${scenarioId}` : '—'} />
              </Box>

              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Enter this PIN on the participant app:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ letterSpacing: 6, mt: 1, mb: 2, fontWeight: 'bold' }}
                >
                  {pin || '———'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Waiting to connect with participant…
                </Typography>
              </Box>
            </Box>
          </Modal>
        </Stack>
      </Box>
    </Box>

    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        zIndex: 2000,
      }}
    >
      <FeedbackButton page="Facilitator live screen" />
    </Box>

    {/* CONFIRM END MODAL */}
    <Modal open={endSessionBtn} onClose={endingScenario ? undefined : closeEndSession}>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 480,
      maxWidth: '95vw',
      bgcolor: 'white',
      borderRadius: 3,
      border: `1px solid ${ACCENT_BORDER}`,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.25,
        bgcolor: ACCENT,
        borderBottom: `1px solid ${ACCENT_BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography sx={{ fontWeight: 800 }}>End scenario</Typography>

      <Chip
        size="small"
        label={room ? `Room ${room}` : `Scenario #${scenarioId || '—'}`}
        sx={{ bgcolor: 'white' }}
      />
    </Box>

    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 1 }}>
        End this live scenario?
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 2 }}>
        This will stop the participant session, save the current data, and take you to the session summary.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          bgcolor: '#F8FAFF',
          mb: 2,
        }}
      >
        <Stack spacing={0.75}>
          <Typography variant="body2">
            <strong>Scenario:</strong> {scenarioId}
          </Typography>

          <Typography variant="body2">
            <strong>Runtime:</strong> {formatHMS(elapsed)}
          </Typography>

          <Typography variant="body2">
            <strong>Participant:</strong> {participantName || '—'}
          </Typography>
        </Stack>
      </Paper>

      <Alert severity="warning">
        This cannot be undone once the summary is generated.
      </Alert>
    </Box>

    <Box
      sx={{
        px: 3,
        pb: 3,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
      }}
    >
      <Button
        variant="outlined"
        onClick={closeEndSession}
        disabled={endingScenario}
        sx={{ textTransform: 'none' }}
      >
        Cancel
      </Button>

      <Button
        variant="contained"
        color="error"
        onClick={handleEndScenario}
        disabled={endingScenario}
        sx={{ textTransform: 'none', minWidth: 140 }}
      >
        {endingScenario ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} color="inherit" />
            <span>Ending…</span>
          </Stack>
        ) : (
          'End scenario'
        )}
      </Button>
    </Box>
  </Box>
</Modal>

    {/* ENDING SCENARIO BLOCK MODAL */}
    <Modal open={endingScenario}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 3,
          boxShadow: 24,
          px: 4,
          py: 3,
          minWidth: 280,
          textAlign: 'center',
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography sx={{ fontWeight: 700 }}>Ending scenario</Typography>
        <Typography variant="body2" color="text.secondary">
          Saving session data and preparing the summary.
        </Typography>
      </Box>
    </Modal>

    {/* CREATE OBS MODAL */}
    <Modal open={addObsOpen} onClose={closeAddObs}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'white',
          borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`,
          boxShadow: 24,
        }}
      >
        {/* Modal header */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>Create observations</Typography>
          <Chip size="small" label={scenarioId ? `Scenario #${scenarioId}` : '—'} />
        </Box>

        {/* Obs set editor */}
        <Box sx={{ p: 2 }}>
        <div style={{display:'flex', flexDirection:'column', width:'100%', marginBottom:'-2rem'}}>
          <div style={{display:'flex', flexDirection:'row', gap:'1rem', alignItems:'center'}}>
              <TextField
                label="Observations set name"
                size='small'
                type="text"
                value={obs.name}
                onChange={(e) => handleChange('name', e.target.value)}
                slotProps={{ textField: { margin: 'normal' } }}
              />
          </div>

          <div style={{display:'flex', flexDirection:'row', gap:'5px', marginBottom:'-1rem'}}>
            <TextField
              label="RR"
              type="number"
              value={obs.rr}
              onChange={(e) => handleChange('rr', e.target.value)}
              margin="normal"
            />
            <TextField
              label="Sats (%)"
              type="number"
              value={obs.spo2}
              onChange={(e) => handleChange('spo2', e.target.value)}
              margin="normal"
            />
            <TextField
              label="O₂ Flow"
              value={obs.o2}
              onChange={(e) => handleChange('o2', e.target.value)}
              margin="normal"
            />
            <TextField
              label="O₂ Device"
              value={obs.device}
              onChange={(e) => handleChange('device', e.target.value)}
              margin="normal"
            />
            </div>

          <div style={{display:'flex', flexDirection:'row', gap:'5px', marginBottom:'-1rem'}}>
            <TextField
              label="SBP (mmHg)"
              type="number"
              value={obs.sbp}
              onChange={(e) => handleChange('sbp', e.target.value)}
              margin="normal"
            />
            <TextField
              label="Pulse"
              type="number"
              value={obs.pulse}
              onChange={(e) => handleChange('pulse', e.target.value)}
              margin="normal"
            />
          </div>
          
          <div style={{display:'flex', flexDirection:'row', gap:'5px', marginBottom:'5px'}}>
            <TextField
              label="AVPU"
              select
              value={obs.avpu}
              onChange={(e) => handleChange('avpu', e.target.value)}
              margin="normal"
              style={{minWidth:'120px'}}
            >
              {['Alert', 'V', 'P', 'U'].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Temp"
              type="number"
              value={obs.temp}
              onChange={(e) => handleChange('temp', e.target.value)}
              margin="normal"
            />
          </div>
        </div>
        </Box>

        {/* Modal footer actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 2, pb: 2 }}>
          <Button onClick={closeAddObs} disabled={savingNewBloods}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color='success'
            onClick={handleSave}
            disabled={!obs.name}
          >
            {savingNewBloods ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Modal>
    {/* CREATE BLOODS MODAL */}
    <Modal open={addBloodsOpen} onClose={closeAddBloods}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 850,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'white',
          borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`,
          boxShadow: 24,
        }}
      >
        {/* Modal header */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>Create blood sets</Typography>
          <Chip size="small" label={scenarioId ? `Scenario #${scenarioId}` : '—'} />
        </Box>

        {/* Blood set editor */}
        <Box sx={{ p: 2 }}>
          <BloodSetEditor
            data={editorData}
            onChange={setEditorData}
            showTimestampPicker={false}
            forcedTimestamp={null}
            buttonLabel="Add blood set"
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              New blood sets
            </Typography>

            {editorData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing added yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {editorData.map((entry, idx) => (
                  <Box
                    key={entry.id || idx}
                    sx={{
                      p: 1.25,
                      border: '1px solid #e5e7eb',
                      borderRadius: 1,
                      bgcolor: '#F8FAFF',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {entry.name || `Set ${idx + 1}`}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={0.5}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ mt: 0.5 }}
                    >
                      {entry.groups.map((g) => (
                        <Box key={g.groupKey} sx={{ mb: 1 }}>
                          <Chip key={g.label} size="medium" label={g.label} />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Box>

        {/* Modal footer actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 2, pb: 2 }}>
          <Button onClick={closeAddBloods} disabled={savingNewBloods}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color='success'
            onClick={handleConfirmAddBloods}
            disabled={savingNewBloods || editorData.length === 0}
          >
            {savingNewBloods ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Modal>
    {/* RESOLVE REQUEST MODAL */}
    <Modal open={resolveOpen} onClose={closeResolve}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 640,
          maxWidth: '95vw',
          bgcolor: 'white',
          borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`,
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>
            Resolve request{resolveReq ? `: ${resolveReq.type}` : ''}
          </Typography>
          {resolveReq?.type && <Chip size="small" label={resolveReq.type.toUpperCase()} />}
        </Box>

        <Box sx={{ p: 2 }}>
          <Box spacing={1} sx={{display:'flex', flexDirection:'row', justifyContent:'center', alignItems:'center', gap:'5px', width:'100%'}}>
            <Autocomplete
                sx={{ my: 1, width:'70%' }}
                options={resolveReq?.type === 'obs' ? obsSets : bloodSets}
                value={resolveSet}
                onChange={(_, v) => setResolveSet(v)}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                getOptionLabel={(o) =>
                resolveReq?.type === 'obs'
                    ? o
                    ? `${o.name}`
                    : ''
                    : o
                    ? `${o.setname}${o.groupnames_csv ? ` (${o.groupnames_csv})` : ''}`
                    : ''
                }
                renderInput={(params) => (
                <TextField {...params} label="Select a set" placeholder="Choose…" />
                )}
                renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ opacity: option.visible ? 0.55 : 1 }}>
                    <span style={{ flex: 1 }}>
                    {resolveReq?.type === 'obs' ? option.name : option.setname}
                    {resolveReq?.type === 'bloods' && option.groupnames_csv
                        ? ` (${option.groupnames_csv})`
                        : ''}
                    </span>
                    {option.visible ? (
                    <Chip size="small" label="Sent" />
                    ) : (
                    <Chip size="small" label="Hidden" variant="outlined" />
                    )}
                </li>
                )}
            />
              <IconButton
                variant="contained"
                size="small"
                color='success'
                sx={{ textTransform: 'none', minWidth:'30px'}}
                disabled={!islive}
                onClick={resolveReq?.type === 'obs' ? openAddObs : openAddBloods}
                >
                <AddBoxOutlinedIcon fontSize='large'></AddBoxOutlinedIcon>
              </IconButton>
          </Box>
        </Box>

        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 2, pb: 2 }}>
          <Button onClick={closeResolve} disabled={resolving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResolveSend}
            disabled={resolving || !resolveSet}
            color='success'
          >
            {resolving ? 'Sending…' : 'Send & fulfil'}
          </Button>
        </Box>
      </Box>
    </Modal>
    {/* SEND IMAGING MODAL */}
    <Modal open={imagingOpen} onClose={closeImagingModal}>
  <>
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 1100,
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflow: 'auto',
        bgcolor: 'white',
        borderRadius: 3,
        border: `1px solid ${ACCENT_BORDER}`,
        boxShadow: 24,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: ACCENT,
          borderBottom: `1px solid ${ACCENT_BORDER}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImageOutlinedIcon />
          <Typography sx={{ fontWeight: 800 }}>Send imaging</Typography>
        </Box>

        <Chip size="small" label={scenarioId ? `Scenario #${scenarioId}` : '—'} />
      </Box>

      <Box sx={{ p: 2, pb: selectedImagingItem ? 10 : 2 }}>
        <ImagingLibraryBrowser
          title="Imaging result"
          subtitle="Search public and uploaded images, then choose one to send to the participant."
          defaultIncludePublic
          defaultIncludeUser
          showSourceToggles
          showSelectedBar={false}
          onSelect={(item) => setSelectedImagingItem(item)}
        />

        {selectedImagingItem && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Selected: <strong>{selectedImagingItem.title}</strong>
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          px: 2,
          pb: 2,
        }}
      >
        <Button onClick={closeImagingModal} disabled={sendingImaging}>
          Cancel
        </Button>
      </Box>
    </Box>

    {selectedImagingItem && (
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 32,
          transform: 'translateX(-50%)',
          zIndex: 2000,
          width: {
            xs: 'calc(100vw - 24px)',
            sm: 620,
            md: 760,
          },
          maxWidth: 'calc(100vw - 24px)',
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(96,165,250,0.7)',
          borderRadius: 999,
          boxShadow: '0 12px 32px rgba(15,23,42,0.25)',
          px: 2,
          py: 1.25,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <CheckCircleIcon color="primary" />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                lineHeight: 1.1,
              }}
            >
              Selected image
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedImagingItem.title}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="success"
            onClick={handleSendImaging}
            disabled={!selectedImagingItem || sendingImaging}
            sx={{
              textTransform: 'none',
              whiteSpace: 'nowrap',
              borderRadius: 999,
              px: 2,
            }}
          >
            {sendingImaging ? 'Sending…' : 'Send imaging'}
          </Button>
        </Stack>
      </Box>
    )}
  </>
</Modal>
  </Box>
);
}

/* ---------- small helpers for consistent cards ---------- */
function SectionCard({ title, subtitle, children }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${ACCENT_BORDER}`, borderRadius: 3, bgcolor: 'white', overflow: 'hidden' }}>
      <Box sx={{
        px: 2, py: 1.25,
        bgcolor: ACCENT,
        borderBottom: `1px solid ${ACCENT_BORDER}`,
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'
      }}>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {children}
    </Paper>
  );
}

function ActionCard({ title, rightIcon, children }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${ACCENT_BORDER}`, borderRadius: 2 }}>
      <Box sx={{
        px: 1.5, py: 1,
        borderBottom: `1px solid ${ACCENT_BORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'white'
      }}>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        {rightIcon || null}
      </Box>
      <Box sx={{ p: 1.5, bgcolor: '#F8FAFF' }}>
        {children}
      </Box>
    </Paper>
  );
}
